import { faChevronsRight, faPlus, faRobot, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { zodResolver } from "@hookform/resolvers/zod";

import { Autocomplete, TextField } from "@mui/material";

import { useForm, Controller } from "react-hook-form";

import { ListAltOutlined, Person2Outlined } from "@mui/icons-material";

import { Button } from "@/components/Button/Button";
import { Icon, PermissionCheck } from "@/components";
import { tasksSelector } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import { ERROR_TYPE, translateError } from "@/utils";

import { useGetAllTagsQuery } from "@/pages/Private/redux/to-do/to-do.api";

import DeleteModal from "@/components/Modals/DeleteModal";

import { ButtonColor } from "@/components/Button/types";

import { PermissionRoles } from "@/enum";

import { useGetCommentsByTaskQuery } from "@/pages/Private/redux/tasks-and-subtasks/task-and-subtasks-activity.api";

import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";
import { sharedSx, sharedInputProps, sharedMenuProps } from "@/styles/sharedComponentStyles";

import { ToDoStatusDropDown } from "./ToDoStatusDropdown";
import {
	UpdateTaskInfo,
	UpdateTaskInfoSchema,
} from "../../../TasksAndSubTasks/schema/tasks-and-subtasks";
import CreateSubTaskInput from "../../../TasksAndSubTasks/components/CreateSubTaskInput";
import Activity from "../../../TasksAndSubTasks/components/Activity";
import { useToDoSocket } from "../providers/SocketContext";

const AiChat = lazy(() => import("../../../TasksAndSubTasks/components/AiChat"));

interface ToDoDetailsProps {
	companyToDoProgressId: number | null;
	close: (task: null) => void;
	showSubTaskDetails?: (subTaskId: number) => void;
}

export default function ToDoDetails({
	close,
	companyToDoProgressId,
	showSubTaskDetails,
}: ToDoDetailsProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const tasks = useSelector(tasksSelector);
	const data = useMemo(
		() =>
			companyToDoProgressId ? tasks.find((item) => item.id === companyToDoProgressId) : undefined,
		[companyToDoProgressId, tasks]
	);

	const dataRef = useRef(data);

	useGetCommentsByTaskQuery(data?.id ?? 0, {
		skip: !data?.id,
	});

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const { data: users } = useGetAssigneesQuery();

	const { updateTask, updateSubTask, deleteTask } = useToDoSocket();

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UpdateTaskInfo>({
		defaultValues: {
			id: data?.id ?? -1,
			title: data?.title,
			description: data?.description,
			complexityEstimate: data?.complexityEstimate,
			status: data?.status,
			priority: data?.priority,
			tagId: data?.tagId ?? null,
			assigneeId: data?.assigneeId,
		},
		resolver: zodResolver(UpdateTaskInfoSchema),
	});

	useEffect(() => {
		if (data) {
			reset({
				id: data.id,
				title: data.title,
				description: data.description,
				complexityEstimate: data.complexityEstimate,
				status: data.status,
				priority: data.priority,
				tagId: data.tagId,
				assigneeId: data.assigneeId,
			});
		}
	}, [
		data?.id,
		data?.assigneeId,
		data?.title,
		data?.description,
		data?.status,
		data?.priority,
		reset,
		data?.tagId,
	]);
	const unassignedOption = {
		firstName: t("createTask.unassigned"),
		lastName: "",
		assigneeId: null as unknown as number,
	};

	const assigneeOptions = [
		unassignedOption,
		...(users?.map((user) => ({
			firstName: user.firstName,
			lastName: user.lastName,
			assigneeId: +user.id,
		})) ?? []),
	];

	const { data: tags } = useGetAllTagsQuery();

	const tagOptions: { tagId: number; tagName: string }[] =
		tags
			?.filter((t): t is { id: number; name: string } => !!t.id)
			.map((t) => ({ tagId: t.id, tagName: t.name })) ?? [];

	const onSubmit = useCallback(
		(formValues: UpdateTaskInfo) => {
			const current = dataRef.current;

			if (!current) {
				return;
			}

			const merged: UpdateTaskInfo = {
				...formValues,
				id: current.id ?? -1,
				status: current.status ?? formValues.status,
			};

			updateTask(merged.id, merged);

			reset(merged);
		},
		[updateTask, updateSubTask, reset, data?.subTasks]
	);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isCreateSubTaskInputShowing, setIsCreateSubTaskInputShowing] = useState(false);
	const [isAiChatOpen, setIsAiChatOpen] = useState(false);

	const handleClose = useCallback(async () => {
		close(null);
	}, [handleSubmit, onSubmit, close]);

	const handleSave = useCallback(async () => {
		await handleSubmit(onSubmit)();
	}, [handleSubmit, onSubmit]);

	const inputRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
				setIsCreateSubTaskInputShowing(false);
			}
		};

		if (isCreateSubTaskInputShowing) {
			document.addEventListener("mousedown", handleClickOutside);
			inputRef?.current?.focus();
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isCreateSubTaskInputShowing]);

	useEffect(() => {
		if (!data?.id) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Escape") return;
			if (e.defaultPrevented) return;
			if (isDeleteModalOpen || isAiChatOpen) return;
			handleClose();
		};
		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [data?.id, isDeleteModalOpen, isAiChatOpen, handleClose]);

	return (
		<>
			<div
				aria-hidden
				className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ease-in-out
					${data ? "opacity-100" : "opacity-0 pointer-events-none"}`}
				onClick={handleClose}
			/>
			<div
				className={`fixed right-0 top-0 z-50 h-[100vh] w-[40vw] bg-white shadow-lg overflow-y-auto
					transition-all duration-300 ease-in-out
					${data ? "translate-x-0 opacity-100" : "translate-x-full opacity-0 pointer-events-none"}`}
			>
			{!!data?.id && (
				<>
					<div className="w-full p-[26px] border-b border-[#E6E6EC] flex items-center justify-between">
						<Button
							className="rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !shadow-none hover:bg-primary-gray-lightest"
							image={<Icon className="text-primary-blue-dark w-5 h-5" icon={faChevronsRight} />}
							title=""
							onClick={handleClose}
						/>
					</div>
					<div className="p-6 w-full space-y-4 border-b border-[#E6E6EC] flex flex-col">
						<div className="w-full flex flex-col gap-2">
							<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
								{t("createTask.status")}
							</p>
							<ToDoStatusDropDown
								className="!m-0 w-fit"
								companyToDoProgressId={data.id}
								isTask={data?.isTask}
							/>
						</div>
						<div className="w-full flex flex-col gap-2">
							<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
								{t("createTask.title")}
							</p>
							<h2 className="w-full flex gap-4 items-start justify-start font-semibold text-[20px] leading-[26px] text-primary-blue-dark">
								<Controller
									control={control}
									name="title"
									render={({ field }) => (
										<TextField
											{...field}
											fullWidth
											error={!!errors?.title}
											helperText={
												!!errors?.title?.message
													? translateError[errors?.title?.message as unknown as ERROR_TYPE]
													: errors?.title?.message
											}
											inputProps={{
												style: { padding: "8px" },
											}}
											size="small"
										/>
									)}
								/>
							</h2>
						</div>
						<div className="w-full flex flex-col gap-2">
							<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
								{t("createTask.description")}
							</p>
							<Controller
								control={control}
								name="description"
								render={({ field }) => (
									<TextField {...field} fullWidth multiline maxRows={6} minRows={6} size="small" />
								)}
							/>
						</div>
						<div className="grid grid-cols-2 gap-4 items-start">
							<div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{t("createTask.tags")}
								</p>
								<Controller
									control={control}
									name="tagId"
									render={({ field }) => (
										<Autocomplete<{ tagId: number; tagName: string }>
											getOptionLabel={(o) => o.tagName}
											isOptionEqualToValue={(a, b) => a.tagId === b.tagId}
											options={tagOptions}
											renderInput={(params) => (
												<TextField
													placeholder={ts("none")}
													{...params}
													InputProps={{
														...params.InputProps,
														startAdornment: (
															<>
																<ListAltOutlined
																	sx={{ color: "action.active", fontSize: 18, mr: 0.5 }}
																/>
																{params.InputProps.startAdornment}
															</>
														),
													}}
													inputProps={{ ...params.inputProps, ...sharedInputProps }}
													sx={sharedSx}
												/>
											)}
											size="small"
											slotProps={{ paper: { sx: sharedMenuProps.PaperProps.sx } }}
											value={tagOptions.find((o) => o.tagId === field.value) ?? null}
											onChange={(_, selected) => field.onChange(selected?.tagId ?? null)}
										/>
									)}
								/>
							</div>
							{/* <div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{ts("priority")}
								</p>
								<ToDoPriorityDropdown isSubtask={!data.isTask} companyToDoProgressId={data.id} />
							</div>

							<div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{t("createTask.complexity")}
								</p>
								<Controller
									control={control}
									name="complexityEstimate"
									render={({ field }) => (
										<TextField
											fullWidth
											select
											SelectProps={{ MenuProps: sharedMenuProps }}
											inputProps={sharedInputProps}
											size="small"
											sx={sharedSx}
											value={field.value}
											onChange={(e) => field.onChange(Number(e.target.value))}
										>
											<MenuItem value={1}>{t("createTask.easy")}</MenuItem>
											<MenuItem value={2}>{t("createTask.normal")}</MenuItem>
											<MenuItem value={3}>{t("createTask.hard")}</MenuItem>
											<MenuItem value={4}>{t("createTask.very-hard")}</MenuItem>
											<MenuItem value={5}>{t("createTask.expert")}</MenuItem>
										</TextField>
									)}
								/>
							</div> */}

							<div className="w-full flex flex-col gap-2">
								<p className="text-[16px] font-bold leading-5 text-primary-blue-dark">
									{t("createTask.assignee")}
								</p>
								<Controller
									control={control}
									name="assigneeId"
									render={({ field }) => (
										<Autocomplete
											disableClearable
											className="w-full"
											defaultValue={
												assigneeOptions.find((o) => o?.assigneeId === data?.assigneeId) ??
												unassignedOption
											}
											getOptionLabel={(o) => `${o?.firstName ?? ""} ${o?.lastName ?? ""}`.trim()}
											isOptionEqualToValue={(a, b) => a?.assigneeId === b?.assigneeId}
											options={assigneeOptions}
											renderInput={(params) => (
												<TextField
													{...params}
													InputProps={{
														...params.InputProps,
														startAdornment: (
															<>
																<Person2Outlined
																	sx={{ color: "action.active", fontSize: 18, mr: 0.5 }}
																/>
																{params.InputProps.startAdornment}
															</>
														),
													}}
													inputProps={{ ...params.inputProps, ...sharedInputProps }}
													sx={sharedSx}
												/>
											)}
											size="small"
											slotProps={{ paper: { sx: sharedMenuProps.PaperProps.sx } }}
											value={
												assigneeOptions.find((o) => o?.assigneeId === field.value) ??
												unassignedOption
											}
											onChange={(_, v) => field.onChange(v?.assigneeId ?? null)}
										/>
									)}
								/>
							</div>
						</div>
						<div className="w-full  flex justify-between">
							<Button
								className="rounded-[12px] !min-w-[44px] text-primary-blue-dark !min-h-[44px] !max-w-fit !py-2  !px-4 my-6 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
								color={ButtonColor.ACTION}
								title={t("createTask.save")}
								onClick={() => {
									handleSave();
								}}
							/>
							<PermissionCheck requiredPermissions={[PermissionRoles.ADMIN]}>
								<Button
									className="rounded-[12px] bg-white !min-w-[44px] text-primary-blue-dark !min-h-[44px] !max-w-fit !py-2 !px-4 my-6 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="text-primary-blue-dark w-5 h-5 mr-1" icon={faTrashCan} />}
									title={t("createTask.deleteTask")}
									onClick={() => {
										if (data?.id) {
											setIsDeleteModalOpen(true);
										}
									}}
								/>
							</PermissionCheck>
						</div>

						{data?.subTasks?.length && data.subTasks.length > 0 ? (
							<div className="w-full ">
								<h3 className="text-[16px] font-bold leading-5 text-primary-blue-dark mb-3">
									{ts("subTasks")}
								</h3>
								<div className="w-full flex flex-col gap-2">
									{data.subTasks.map((subTask) => (
										<div
											key={subTask?.id}
											className="cursor-pointer rounded-lg hover:bg-gray-50 p-1 transition-colors"
											onClick={() => {
												if (subTask.id) {
													close(null);
													showSubTaskDetails?.(subTask.id);
												}
											}}
										>
											<span className="text-[14px] pl-1 font-semibold text-left leading-5 text-primary-blue-dark mb-2">
												{subTask?.title ? subTask?.title : null}
											</span>
										</div>
									))}
								</div>
							</div>
						) : null}
						<>
							<div
								className={`w-full rounded-md  max-h-full h-[60px] hover:bg-black/5 hover:cursor-pointer flex pl-6 items-center text-gray-700 ${
									isCreateSubTaskInputShowing ? "hidden" : ""
								}`}
								onClick={(e) => {
									e.stopPropagation();
									setIsCreateSubTaskInputShowing(true);
									inputRef.current?.focus();
								}}
							>
								<Icon className="text-md mr-1" icon={faPlus} />
								<p className="text-[16px] text-[#777D8D] text-center py-4">
									{t("createTask.createSubtask")}
								</p>
							</div>
							{isCreateSubTaskInputShowing && (
								<div className="flex justify-center ">
									<CreateSubTaskInput
										inputRef={inputRef ?? null}
										taskData={data}
										taskId={data.id}
										onClose={() => setIsCreateSubTaskInputShowing(false)}
									/>
								</div>
							)}
						</>
					</div>

					<div className="w-full px-6 pt-2 pb-1">
						<Button
							className="rounded-[12px] bg-white !min-w-fit !min-h-[44px] !max-w-fit !py-2 !px-4 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="text-primary-blue-hover w-5 h-5 mr-2" icon={faRobot} />}
							title={t("aiChat.toggle", "AI Assistant")}
							onClick={() => setIsAiChatOpen((v) => !v)}
						/>
					</div>

					<Suspense fallback={null}>
						<AiChat isOpen={isAiChatOpen} taskId={data.id} onClose={() => setIsAiChatOpen(false)} />
					</Suspense>

					<div className="w-full p-6 !pb-3">
						<Activity companyToDoProgressId={companyToDoProgressId} isTask={data.isTask} />
					</div>
				</>
			)}
			<DeleteModal
				description={t("manage-to-dos.delete-description", { taskTitle: data?.title })}
				handleClose={() => setIsDeleteModalOpen(false)}
				handleDelete={() => {
					if (data && data.id) {
						deleteTask(data.id);
						setIsDeleteModalOpen(false);
					}
				}}
				isShowing={isDeleteModalOpen}
				title={t("manage-to-dos.delete-title")}
			/>
			</div>
		</>
	);
}
